/**
 * DimensionSelector Component (Story 5.34, Task 3)
 *
 * Renders a dimension "Group By" dropdown grouped by dimension type.
 * Only shown for widget types that support grouping: bar-chart, pie-chart, data-table.
 */

import type { DimensionDefinition } from "@provenleap/integrations";
import { useMemo } from "react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectGroup, SelectLabel, SelectItem } from "@provenleap/ui";

/** Sentinel value for the "No grouping" option (Radix Select can't use ""). */
const NO_GROUPING = "__none__";

interface DimensionSelectorProps {
  dimensions: DimensionDefinition[];
  selectedDimension?: { key: string; label: string; type: string };
  isLoading: boolean;
  onSelect: (key: string, label: string, type: string) => void;
}

/** Group dimensions by type for dropdown display */
function groupDimensionsByType(
  dimensions: DimensionDefinition[],
): Array<{ type: string; dimensions: DimensionDefinition[] }> {
  const groups: Record<string, DimensionDefinition[]> = {};

  for (const dim of dimensions) {
    if (!groups[dim.type]) groups[dim.type] = [];
    groups[dim.type]!.push(dim);
  }

  // Sort: Time first, then alphabetical
  const typeOrder = ["Time", "Traffic", "Geo", "Demo", "Tech", "Content", "Structure", "Search", "Links", "Audience"];
  return Object.entries(groups)
    .sort(([a], [b]) => {
      const ai = typeOrder.indexOf(a);
      const bi = typeOrder.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    })
    .map(([type, dims]) => ({
      type,
      dimensions: dims.sort((a, b) => a.label.localeCompare(b.label)),
    }));
}

export function DimensionSelector({
  dimensions,
  selectedDimension,
  isLoading,
  onSelect,
}: DimensionSelectorProps) {
  const groupedDimensions = useMemo(
    () => groupDimensionsByType(dimensions),
    [dimensions],
  );

  if (isLoading) {
    return (
      <div data-testid="dimension-selector" className="space-y-1">
        <label className="block text-[11px] font-semibold uppercase tracking-wider text-foreground/70 mb-1.5">Group By</label>
        <div className="w-full px-1.5 py-1 text-xs text-muted-foreground">Loading dimensions...</div>
      </div>
    );
  }

  if (dimensions.length === 0) return null;

  return (
    // Label-left + 130px control, matching the Appearance fields.
    <div data-testid="dimension-selector" className="grid grid-cols-[1fr_130px] items-center gap-2">
      <label className="min-w-0 text-[13px] text-foreground">Group by</label>
      <Select
        value={selectedDimension?.key ?? NO_GROUPING}
        onValueChange={(v) => {
          if (v === NO_GROUPING) {
            onSelect("", "", "");
            return;
          }
          const dim = dimensions.find((d) => d.key === v);
          if (dim) onSelect(dim.key, dim.label, dim.type);
        }}
      >
        <SelectTrigger data-testid="dimension-select" className="touch-target-sm h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]">
          <SelectValue placeholder="No grouping" />
        </SelectTrigger>
        <SelectContent side="left" align="start" sideOffset={8} arrow>
          <SelectItem value={NO_GROUPING} variant="section">No grouping</SelectItem>
          {groupedDimensions.map((group) => (
            <SelectGroup key={group.type}>
              <SelectLabel className="pl-2">{group.type}</SelectLabel>
              {group.dimensions.map((dim) => (
                <SelectItem key={dim.key} value={dim.key} variant="section">
                  {dim.label}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
